import type {
  TargetareImportRand,
  TargetareStareContact,
  TargetareTipContact,
} from "@/types/targetare";

const TIPURI: TargetareTipContact[] = ["email", "telefon", "site", "linkedin"];
const STARI: TargetareStareContact[] = [
  "nevalidat",
  "valid",
  "invalid",
  "bounce",
  "opt_out",
];

export type RezultatParsareCsv = {
  randuri: TargetareImportRand[];
  erori: { linie: number; motiv: string }[];
};

function despartLinie(linie: string, separator: string): string[] {
  const celule: string[] = [];
  let curent = "";
  let inGhilimele = false;
  for (let i = 0; i < linie.length; i++) {
    const ch = linie[i];
    if (ch === '"') {
      if (inGhilimele && linie[i + 1] === '"') {
        curent += '"';
        i++;
      } else {
        inGhilimele = !inGhilimele;
      }
    } else if (ch === separator && !inGhilimele) {
      celule.push(curent);
      curent = "";
    } else {
      curent += ch;
    }
  }
  celule.push(curent);
  return celule.map((c) => c.trim());
}

function detecteazaSeparator(antet: string): string {
  const candidati = [",", ";", "\t"];
  return candidati.reduce((best, sep) =>
    antet.split(sep).length > antet.split(best).length ? sep : best
  );
}

function deduceTip(valoare: string): TargetareTipContact {
  if (valoare.includes("@")) return "email";
  if (/linkedin\.com/i.test(valoare)) return "linkedin";
  if (/^[+\d][\d\s.\-()]{6,}$/.test(valoare)) return "telefon";
  return "site";
}

export function parseazaCsvContacte(text: string): RezultatParsareCsv {
  const linii = text
    .replace(/^﻿/, "")
    .split(/\r?\n/)
    .filter((l) => l.trim().length > 0);
  const rezultat: RezultatParsareCsv = { randuri: [], erori: [] };
  if (linii.length === 0) return rezultat;
  const separator = detecteazaSeparator(linii[0]);
  const antet = despartLinie(linii[0], separator).map((c) =>
    c.toLowerCase().replace(/[^a-z_]/g, "")
  );
  const idx = (nume: string[]) => antet.findIndex((c) => nume.includes(c));
  const iCui = idx(["cui", "cif", "codfiscal"]);
  const iValoare = idx(["valoare", "email", "adresa", "contact", "telefon"]);
  const iTip = idx(["tip", "type"]);
  const iSursa = idx(["sursa", "source"]);
  const iStare = idx(["stare", "status"]);
  const iNote = idx(["note", "nota", "observatii"]);
  if (iCui < 0 || iValoare < 0) {
    rezultat.erori.push({
      linie: 1,
      motiv:
        "Antetul trebuie să conțină coloanele „cui” și „valoare” (sau „email”).",
    });
    return rezultat;
  }
  linii.slice(1).forEach((linie, i) => {
    const celule = despartLinie(linie, separator);
    const cui = (celule[iCui] ?? "").replace(/^\s*RO/i, "").trim();
    const valoare = (celule[iValoare] ?? "").trim();
    if (!/^\d{1,10}$/.test(cui)) {
      rezultat.erori.push({ linie: i + 2, motiv: `CUI invalid: „${cui}”` });
      return;
    }
    if (!valoare) {
      rezultat.erori.push({ linie: i + 2, motiv: "valoare lipsă" });
      return;
    }
    const tipBrut = iTip >= 0 ? celule[iTip]?.toLowerCase() : "";
    const tip = TIPURI.includes(tipBrut as TargetareTipContact)
      ? (tipBrut as TargetareTipContact)
      : deduceTip(valoare);
    const stareBruta = iStare >= 0 ? celule[iStare]?.toLowerCase() : "";
    const rand: TargetareImportRand = { cui, tip, valoare };
    if (iSursa >= 0 && celule[iSursa]) rand.sursa = celule[iSursa];
    if (STARI.includes(stareBruta as TargetareStareContact)) {
      rand.stare = stareBruta as TargetareStareContact;
    }
    if (iNote >= 0 && celule[iNote]) rand.note = celule[iNote];
    rezultat.randuri.push(rand);
  });
  return rezultat;
}

export function imparteInLoturi<T>(elemente: T[], marime: number): T[][] {
  const loturi: T[][] = [];
  for (let i = 0; i < elemente.length; i += marime) {
    loturi.push(elemente.slice(i, i + marime));
  }
  return loturi;
}
